import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { concatMap, filter, MonoTypeOperatorFunction, of, tap } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import { getRouterSelectors, ROUTER_NAVIGATED } from '@ngrx/router-store';
import { CallCentreStatusActions } from '@cp/shared/data-access/+state/actions/call-centre-status.actions';
import { ComplianceSummaryActions } from '@cp/shared/data-access/+state/actions/compliance-summary.actions';
import { UrlService } from '../services/url.service';
import { isAuthenticatedAndHealthy$ } from './helpers';

@Injectable()
export class RouterEffects {
  private _routerSelectors = getRouterSelectors();

  public loadCallCentreStatus$ = createEffect(() =>
    this._actions$.pipe(
      this._onRouterNavigated(CallCentreStatusActions.loadCallCentreStatus()),
    ),
  );

  public loadComplianceSummary$ = createEffect(() =>
    this._actions$.pipe(
      this._onRouterNavigated(ComplianceSummaryActions.loadComplianceSummary()),
    ),
  );

  public updateCurrentUrl$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ROUTER_NAVIGATED),
        concatMap(() => this._store.select(this._routerSelectors.selectUrl)),
        tap((url) => {
          this._urlService.updateCurrentUrl(url);
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private _actions$: Actions,
    private _store: Store,
    private _urlService: UrlService,
  ) {}

  private _onRouterNavigated(
    actionToDispatch: Action,
  ): MonoTypeOperatorFunction<Action> {
    return (source) =>
      source.pipe(
        ofType(ROUTER_NAVIGATED),
        isAuthenticatedAndHealthy$(),
        concatLatestFrom(() =>
          this._store.select(this._routerSelectors.selectUrl),
        ),
        filter(([, url]) => {
          const ignoreUrls: ReadonlyArray<string> = [
            '/logged-in',
            '/callback',
            '/log-out',
          ];
          return !ignoreUrls.some((route) => route.startsWith(url));
        }),
        concatMap(() => of(actionToDispatch)),
      );
  }
}
